import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Phone, Mail, Globe, MapPin, ExternalLink, Star, MessageSquare, Award, Calendar, Video, HeartHandshake, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import ContactInfoModal from "@/components/ContactInfoModal";
import DoctorMessageModal from "@/components/DoctorMessageModal";
import ReviewModal from "@/components/ReviewModal";
import ConsultModal from "@/components/ConsultModal";
import ProviderClaimModal from "@/components/ProviderClaimModal";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// NOTE (OIG anti-kickback compliance): Provider-facing paid tiers are NEVER
// surfaced to families in the search UI — no badges, no size hierarchy, no
// gradient borders, no "Featured/Top pick/Sponsored" markers, no preferential
// ordering. Sort is alphabetical (server-side). Objective Medicare CMS star
// ratings still display because they are publicly-available quality data,
// not payment-related. See /app/memory/PROVIDER_TIER_POLICY.md.

export default function ProviderSearch({ careType, sessionId, autoSearchLocation, recommendedCareTypes = [], narrowFilters = null }) {
  const [searchInput, setSearchInput] = useState("");
  const [providers, setProviders] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showDoctorModal, setShowDoctorModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showConsultModal, setShowConsultModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [userContactInfo, setUserContactInfo] = useState(null);
  const [providerReviews, setProviderReviews] = useState({});
  const [chosenIds, setChosenIds] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/portal/chosen-providers`)
      .then((res) => { if (isMountedRef.current) setChosenIds(res.data.map((p) => p.id)); })
      .catch(() => {});
  }, [user]);

  // Auto-fill + auto-run search when Asher detects a zip/city in the chat
  useEffect(() => {
    if (!autoSearchLocation?.value) return;
    setSearchInput(autoSearchLocation.value);
    handleSearch(autoSearchLocation.value);
  }, [autoSearchLocation?.timestamp]);

  const handleChooseProvider = async (provider) => {
    if (!user) {
      toast.info("Sign in to your secure portal to choose providers for your care plan");
      navigate("/auth");
      return;
    }
    const isChosen = chosenIds.includes(provider.id);
    try {
      if (isChosen) {
        await axios.delete(`${API}/portal/providers/${provider.id}/choose`);
        setChosenIds((prev) => prev.filter((id) => id !== provider.id));
        toast.success(`${provider.name} removed from your care plan`);
      } else {
        await axios.post(`${API}/portal/providers/${provider.id}/choose`);
        setChosenIds((prev) => [...prev, provider.id]);
        toast.success(`${provider.name} added to your care plan! View it in your portal.`);
      }
    } catch (error) {
      toast.error("Failed to update your care plan. Please try again.");
    }
  };

  const handleSearch = async (explicitInput) => {
    // Defensive: when called from onClick the MouseEvent is passed as arg;
    // when called from useEffect the raw value comes through. Only trust
    // strings — anything else falls back to the input state, which is
    // itself coerced to a string before we call .trim() on it.
    const seedRaw = typeof explicitInput === "string" ? explicitInput : searchInput;
    const seed = typeof seedRaw === "string" ? seedRaw : String(seedRaw ?? "");
    const input = seed.trim();
    if (!input) {
      toast.error("Please enter a zip code or town/city name");
      return;
    }

    // Build search payload: 5-digit zip OR "City" / "City, ST"
    // If Asher has recommended specific types, prefer that list — it's more accurate than the
    // single `careType` which collapses multi-type recommendations to "all".
    const payload = { care_type: careType };
    if (Array.isArray(recommendedCareTypes) && recommendedCareTypes.length > 0) {
      payload.care_types = recommendedCareTypes;
    }
    // Narrow-down filters (family checked boxes before typing zip). Empty
    // arrays are still forwarded so the backend treats the request as "filters
    // active" — no filters at all sends nothing and keeps the legacy contract.
    if (narrowFilters) {
      const { insurance_filters = [], program_filters = [], service_filters = [] } = narrowFilters;
      if (insurance_filters.length || program_filters.length || service_filters.length) {
        payload.insurance_filters = insurance_filters;
        payload.program_filters = program_filters;
        payload.service_filters = service_filters;
      }
    }
    if (/^\d{5}$/.test(input)) {
      payload.zip_code = input;
    } else if (/^\d+$/.test(input)) {
      toast.error("Please enter a valid 5-digit zip code");
      return;
    } else {
      const [cityPart, statePart] = input.split(",").map((s) => s.trim());
      payload.city = cityPart;
      if (statePart) payload.state = statePart;
    }

    setIsSearching(true);
    setHasSearched(true);

    try {
      const response = await axios.post(`${API}/providers/search`, payload);

      // Bail if the component unmounted (or a newer search superseded us)
      // before the response landed — prevents late setState leaks.
      if (!isMountedRef.current) return;

      // Normalize the response — backend returns a bare array when no filters
      // were sent, or {providers, no_filter_matches, filters_applied} when the
      // family used the narrow-down card. Either way we hand `list` to the UI.
      const payloadResp = response.data;
      const list = Array.isArray(payloadResp) ? payloadResp : (payloadResp?.providers || []);
      const noFilterMatches = !Array.isArray(payloadResp) && !!payloadResp?.no_filter_matches;

      setProviders(list);

      // Load reviews for each provider
      for (const provider of list) {
        loadProviderReviews(provider.id);
      }

      // Gentle heads-up when filters yielded nothing so we fell back to all providers.
      if (noFilterMatches && list.length > 0) {
        toast.info("No exact filter matches — showing everyone in your area so you don't miss anyone.");
      }

      if (list.length === 0) {
        const filterLabel =
          (recommendedCareTypes && recommendedCareTypes.length > 1)
            ? recommendedCareTypes.join(" and ")
            : (careType && careType !== "all" ? careType : null);
        toast.info(
          filterLabel
            ? `No ${filterLabel} providers found for "${input}". Try a different zip or care type.`
            : `No providers found for "${input}". Try a different zip or town.`
        );
      } else {
        // Build a context-rich toast: count + care type + location + (for advisors) compensation mix
        const total = list.length;
        const filterLabel =
          (recommendedCareTypes && recommendedCareTypes.length > 1)
            ? `${recommendedCareTypes.join(" and ")} `
            : (careType && careType !== "all" ? `${careType} ` : "");
        const typeLabel = filterLabel;
        const locationLabel = payload.zip_code
          ? payload.zip_code
          : payload.state
            ? `${payload.city}, ${payload.state}`
            : payload.city;

        let extra = "";
        if (careType === "Senior Advisor") {
          const independent = list.filter(p => p.compensation_model === "independent").length;
          const communityPaid = list.filter(p => p.compensation_model === "community_paid").length;
          const parts = [];
          if (independent) parts.push(`${independent} independent`);
          if (communityPaid) parts.push(`${communityPaid} community-paid`);
          if (parts.length) extra = ` — ${parts.join(", ")}`;
        }

        toast.success(`Found ${total} ${typeLabel}provider${total === 1 ? "" : "s"} in ${locationLabel}${extra}`);
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error("Error searching providers:", error);
      const detail = error.response?.data?.detail;
      if (detail) {
        setProviders([]);
        toast.error(detail);
      } else {
        toast.error("Failed to search providers. Please try again.");
      }
    } finally {
      if (isMountedRef.current) setIsSearching(false);
    }
  };

  const loadProviderReviews = async (providerId) => {
    try {
      const response = await axios.get(`${API}/reviews/provider/${providerId}`);
      if (!isMountedRef.current) return;
      setProviderReviews(prev => ({
        ...prev,
        [providerId]: response.data
      }));
    } catch (error) {
      console.error("Error loading reviews:", error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleContactProvider = (provider) => {
    setSelectedProvider(provider);
    if (!userContactInfo) {
      setShowContactModal(true);
    } else {
      // Already have contact info, proceed directly
      toast.success("You can now contact the provider directly");
    }
  };

  const handleContactInfoSubmit = async (contactInfo) => {
    try {
      await axios.post(`${API}/chat/submit-contact-info`, {
        session_id: sessionId,
        ...contactInfo
      });
      setUserContactInfo(contactInfo);
      setShowContactModal(false);
      toast.success("Contact information saved! You can now connect with providers.");
    } catch (error) {
      console.error("Error saving contact info:", error);
      toast.error("Failed to save contact information");
    }
  };

  const handleSendToDoctor = (provider) => {
    if (!userContactInfo) {
      setSelectedProvider(provider);
      setShowContactModal(true);
      return;
    }
    setSelectedProvider(provider);
    setShowDoctorModal(true);
  };

  const handleDoctorMessageSubmit = async (messageData) => {
    try {
      await axios.post(`${API}/doctor-message/send`, {
        session_id: sessionId,
        provider_id: selectedProvider.id,
        user_name: userContactInfo.full_name,
        user_email: userContactInfo.email,
        user_phone: userContactInfo.phone,
        doctor_email: messageData.doctor_email,
        personal_message: messageData.personal_message
      });
      setShowDoctorModal(false);
      toast.success("Message sent to your doctor/case manager!");
    } catch (error) {
      console.error("Error sending doctor message:", error);
      toast.error("Failed to send message");
    }
  };

  const handleReviewProvider = (provider) => {
    if (!userContactInfo) {
      setSelectedProvider(provider);
      setShowContactModal(true);
      return;
    }
    setSelectedProvider(provider);
    setShowReviewModal(true);
  };

  const handleReviewSubmit = async (reviewData) => {
    try {
      await axios.post(`${API}/reviews/create`, {
        provider_id: selectedProvider.id,
        user_email: userContactInfo.email,
        user_name: userContactInfo.full_name,
        rating: reviewData.rating,
        review_text: reviewData.review_text
      });
      setShowReviewModal(false);
      toast.success("Review submitted successfully!");
      // Reload reviews
      loadProviderReviews(selectedProvider.id);
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Failed to submit review");
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? "fill-secondary text-secondary" : "text-gray-300"
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Asher's recommendation chip */}
      {recommendedCareTypes.length > 0 && (
        <div
          data-testid="asher-recommendation-banner"
          className="flex items-start gap-3 rounded-xl border border-[#2AADA0]/30 bg-gradient-to-r from-[#2AADA0]/8 to-[#264653]/5 px-4 py-3 md:px-5 md:py-4"
        >
          <div className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-full bg-white border border-[#2AADA0]/30 flex items-center justify-center shadow-sm">
            <img
              src="/tyvena-mark-512.png"
              alt="Asher"
              className="w-7 h-7 rounded-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-body font-semibold uppercase tracking-wider text-[#2AADA0]">
              Asher&apos;s recommendation
            </p>
            <p className="text-sm md:text-base text-text-primary font-body mt-1 leading-snug">
              Showing{" "}
              {recommendedCareTypes.map((t, i) => (
                <span key={t}>
                  <span className="font-semibold text-primary">{t}</span>
                  {i < recommendedCareTypes.length - 2 && ", "}
                  {i === recommendedCareTypes.length - 2 && (recommendedCareTypes.length > 2 ? ", and " : " and ")}
                </span>
              ))}{" "}
              based on what you shared with Asher.
            </p>
          </div>
        </div>
      )}

      {/* Search Section */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-6 md:p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-heading font-semibold text-text-primary mb-2">
            Find Care Providers Near You
          </h2>
          <p className="text-base text-text-secondary font-body">
            {recommendedCareTypes && recommendedCareTypes.length > 1
              ? `Showing ${recommendedCareTypes.join(" and ")} providers in your area`
              : careType && careType !== "all"
              ? `Showing ${careType} providers in your area`
              : careType === "all"
              ? "Showing all recommended care providers in your area"
              : "Enter your zip code or town to see available care providers"}
          </p>
        </div>

        <div className="flex gap-3">
          <div className="flex-1">
            <Input
              data-testid="zip-code-input"
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Zip code or town (e.g., 29577 or Myrtle Beach, SC)"
              className="h-12 md:h-14 text-lg rounded-lg border-2 border-input focus:border-primary focus:ring-0 bg-white font-body"
            />
          </div>
          <Button
            data-testid="search-providers-button"
            onClick={handleSearch}
            disabled={isSearching}
            className="min-h-[48px] min-w-[48px] h-12 md:h-14 px-6 bg-primary text-white hover:bg-[#238C82] rounded-full font-bold shadow-md hover:shadow-lg transition-all duration-300 active:scale-95 font-body"
          >
            {isSearching ? (
              <span>Searching...</span>
            ) : (
              <>
                <Search className="w-5 h-5 mr-2" />
                Search
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Providers List */}
      {(isSearching || hasSearched) && (
        <div data-testid="providers-list" className="space-y-4">
          {isSearching ? (
            <LighthouseLoader />
          ) : providers.length === 0 ? (
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 text-center">
              <p className="text-lg text-text-secondary font-body">
                No providers found for &ldquo;{searchInput}&rdquo;.
                {careType && careType !== "all" && ` Try searching without filtering by ${careType}.`}
              </p>
            </div>
          ) : (
            <>
              {/* Patient-Choice reminder — sits above the results so families
                  see this before scanning any card. Medicare guarantees the
                  right to choose your provider; Tyvena exists to support that. */}
              <div
                data-testid="patient-choice-reminder"
                className="rounded-2xl border border-primary/20 bg-gradient-to-br from-[#EDF7F6] to-white p-5 sm:p-6 shadow-sm"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <HeartHandshake className="w-5 h-5 text-primary" aria-hidden="true" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-accent text-base sm:text-lg mb-1.5">
                      This is your choice — no pressure, only support.
                    </h3>
                    <p className="font-body text-sm sm:text-base text-text-secondary leading-relaxed">
                      Tyvena empowers families to make the right choices for the ones they love, based on their individual needs. Only support, encouragement, information, and connection — all on your terms. <span className="font-semibold text-text-primary">No one will ever contact you unless you reach out to them first.</span>
                    </p>
                  </div>
                </div>
              </div>
              {providers.map((provider) => {
              const tier = provider.subscription?.tier || "listed";
              const isPremium = tier === "premium";
              const isEnhanced = tier === "enhanced";

              return (
              <div
                key={provider.id}
                data-testid={`provider-card-${provider.id}`}
                className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-6 transition-all duration-300 provider-card hover:shadow-md"
              >
                <div className="flex flex-col gap-6">
                  {/* Header — name + objective star rating only. NO tier badge. */}
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-heading font-semibold text-text-primary text-xl">
                          {provider.name}
                        </h3>
                      </div>
                      {/* Rating */}
                      {provider.average_rating > 0 && (
                        <div className="flex items-center gap-2 mb-3">
                          {renderStars(Math.round(provider.average_rating))}
                          <span className="text-sm text-text-secondary font-body">
                            {provider.average_rating.toFixed(1)}{" "}
                            {provider.review_count > 0
                              ? `(${provider.review_count} reviews)`
                              : "· Medicare Quality Star Rating"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-text-secondary font-body text-base">
                    {provider.description}
                  </p>

                  {/* Filter-match pills (narrow-down feature). Only appears when the
                      family used the pre-search filter card AND this provider satisfies
                      at least one of the selected filters. Keeps the UI honest without
                      shouting. */}
                  {Array.isArray(provider.filter_matches) && provider.filter_matches.length > 0 && (
                    <div
                      className="flex flex-wrap items-center gap-2"
                      data-testid={`provider-filter-matches-${provider.id}`}
                    >
                      <span className="text-xs font-body text-text-secondary">Matches:</span>
                      {provider.filter_matches.map((m, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 text-xs font-body"
                        >
                          <CheckCircle2 className="w-3 h-3" />
                          {m}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Services (Enhanced + Premium show their own list on their profile) */}
                  {(isEnhanced || isPremium) && provider.services_offered && provider.services_offered.length > 0 && (
                    <div className="bg-[#F8FAFC] rounded-lg p-6 border border-[#E2E8F0]">
                      <h4 className="text-base font-heading font-bold text-text-primary mb-3">
                        Services We Provide
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {provider.services_offered.map((service, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="text-primary">•</span>
                            <span className="text-sm font-body text-text-primary">{service}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Insurance (Enhanced + Premium) */}
                  {(isEnhanced || isPremium) && provider.insurance_accepted && provider.insurance_accepted.length > 0 && (
                    <div className="bg-white rounded-lg p-6 border border-[#E2E8F0]">
                      <h4 className="text-base font-heading font-bold text-text-primary mb-3">
                        Insurance Accepted
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {provider.insurance_accepted.map((insurance, idx) => (
                          <span key={idx} className="px-3 py-1 bg-primary/10 text-primary text-sm font-body rounded-lg">
                            {insurance}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Care types */}
                  <div className="flex flex-wrap gap-2">
                    {provider.care_types.map((type, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-accent/10 text-accent text-sm font-body font-semibold rounded-full"
                      >
                        {type}
                      </span>
                    ))}
                  </div>

                  {/* Community Partner badge — nonprofit / mission-driven free listing */}
                  {provider.is_community_partner && (
                    <div
                      data-testid={`community-partner-badge-${provider.id}`}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-body font-semibold border bg-secondary/10 text-secondary border-secondary/30 w-fit"
                      title="A trusted nonprofit or mission-driven organization partnering with Tyvena to support families — services are free or donation-based."
                    >
                      <HeartHandshake className="w-3.5 h-3.5" />
                      Community Partner
                    </div>
                  )}

                  {/* Senior Advisor compensation badge */}
                  {provider.care_types.includes("Senior Advisor") && provider.compensation_model && (
                    <div
                      data-testid={`advisor-comp-${provider.id}`}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-body font-semibold border w-fit ${
                        provider.compensation_model === "independent"
                          ? "bg-[#264653]/5 text-[#264653] border-[#264653]/30"
                          : "bg-[#2AADA0]/10 text-[#2AADA0] border-[#2AADA0]/30"
                      }`}
                      title={
                        provider.compensation_model === "independent"
                          ? "This advisor is paid directly by you for fully independent guidance — no referral fees from communities."
                          : "This advisor is free to your family — they're paid by the communities or services they refer you to."
                      }
                    >
                      <ShieldCheck className="w-3.5 h-3.5" />
                      {provider.compensation_model === "independent"
                        ? "Private pay (independent)"
                        : "Community-paid (free to you)"}
                    </div>
                  )}

                  {/* Contact info */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 text-text-secondary">
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm font-body">{provider.address}</span>
                    </div>
                    {provider.matched_zip_codes && provider.matched_zip_codes.length > 0 && (
                      <div className="flex items-center gap-3 text-green-700" data-testid={`matched-zips-${provider.id}`}>
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm font-body font-semibold">
                          Services your area: {provider.matched_zip_codes.join(", ")}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-text-secondary">
                      <Phone className="w-4 h-4 flex-shrink-0" />
                      <a
                        href={`tel:${provider.phone}`}
                        className="text-sm font-body hover:text-primary transition-colors"
                      >
                        {provider.phone}
                      </a>
                    </div>
                    <div className="flex items-center gap-3 text-text-secondary">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <a
                        href={`mailto:${provider.email}`}
                        className="text-sm font-body hover:text-primary transition-colors"
                      >
                        {provider.email}
                      </a>
                    </div>
                    {provider.website && (
                      <div className="flex items-center gap-3 text-text-secondary">
                        <Globe className="w-4 h-4 flex-shrink-0" />
                        <a
                          href={provider.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-body hover:text-primary transition-colors flex items-center gap-1"
                        >
                          Visit Website
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Reviews Section */}
                  {providerReviews[provider.id] && providerReviews[provider.id].length > 0 && (
                    <div className="border-t border-[#E2E8F0] pt-4">
                      <h4 className="text-sm font-heading font-semibold text-text-primary mb-3">
                        Recent Reviews
                      </h4>
                      <div className="space-y-3">
                        {providerReviews[provider.id].slice(0, 2).map((review) => (
                          <div key={review.id} className="bg-background/50 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                {renderStars(review.rating)}
                                <span className="text-xs font-body font-semibold text-text-primary">
                                  {review.user_name}
                                </span>
                              </div>
                              <span className="text-xs text-text-secondary font-body">
                                {new Date(review.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-text-secondary font-body">
                              {review.review_text}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action buttons — IDENTICAL across all tiers (OIG compliance).
                      Virtual Consult is a Premium-tier FEATURE (not preferential
                      placement), so it only renders when the provider offers it. */}
                  <div className="flex flex-wrap gap-3 pt-4 border-t border-[#E2E8F0]">
                    <a
                      href={`tel:${provider.phone}`}
                      data-testid={`click-to-call-${provider.id}`}
                      className="inline-flex items-center justify-center min-h-[48px] px-6 bg-primary text-white hover:bg-[#238C82] rounded-full font-bold shadow-md hover:shadow-lg transition-all duration-300 active:scale-95 font-body"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Call {provider.phone}
                    </a>
                    <Button
                      data-testid={`contact-provider-${provider.id}`}
                      onClick={() => handleContactProvider(provider)}
                      variant="outline"
                      className="min-h-[48px] px-6 border-2 border-primary text-primary hover:bg-primary/10 rounded-full font-bold font-body"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Request Info
                    </Button>
                    {isPremium && (
                      <Button
                        data-testid={`schedule-meeting-${provider.id}`}
                        onClick={() => { setSelectedProvider(provider); setShowConsultModal(true); }}
                        className="min-h-[48px] px-6 bg-accent text-white hover:bg-accent/90 rounded-full font-bold shadow-md hover:shadow-lg transition-all duration-300 active:scale-95 font-body"
                      >
                        <Video className="w-4 h-4 mr-2" />
                        Virtual Consult
                      </Button>
                    )}

                    {/* Common actions for everyone */}
                    <Button
                      data-testid={`choose-provider-${provider.id}`}
                      onClick={() => handleChooseProvider(provider)}
                      className={`min-h-[48px] px-6 rounded-full font-bold shadow-md transition-all duration-300 active:scale-95 font-body ${
                        chosenIds.includes(provider.id)
                          ? "bg-green-600 text-white hover:bg-green-700"
                          : "bg-accent text-white hover:bg-accent/90"
                      }`}
                    >
                      {chosenIds.includes(provider.id) ? (
                        <><CheckCircle2 className="w-4 h-4 mr-2" /> Chosen ✓</>
                      ) : (
                        <><HeartHandshake className="w-4 h-4 mr-2" /> Choose This Provider</>
                      )}
                    </Button>
                    <Button
                      data-testid={`send-to-doctor-${provider.id}`}
                      onClick={() => handleSendToDoctor(provider)}
                      variant="outline"
                      className="min-h-[48px] px-6 border-2 border-primary text-primary hover:bg-primary/10 rounded-full font-bold font-body"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Send to Doctor
                    </Button>
                    <Button
                      data-testid={`review-provider-${provider.id}`}
                      onClick={() => handleReviewProvider(provider)}
                      variant="outline"
                      className="min-h-[48px] px-6 border-2 border-secondary text-secondary hover:bg-secondary/10 rounded-full font-bold font-body"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Write Review
                    </Button>
                  </div>

                  {/* Claim link — only for CMS-imported, unclaimed listings */}
                  {provider.source === "cms_import" && !provider.claimed_by && (
                    <div className="pt-2 -mt-2 text-center md:text-left">
                      <button
                        type="button"
                        data-testid={`claim-listing-${provider.id}`}
                        onClick={() => { setSelectedProvider(provider); setShowClaimModal(true); }}
                        className="text-xs font-body text-text-secondary hover:text-primary underline decoration-dotted underline-offset-2"
                      >
                        Is this your business? Claim this listing.
                      </button>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
            </>
          )}
        </div>
      )}

      {/* Modals */}
      <ContactInfoModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        onSubmit={handleContactInfoSubmit}
      />
      <DoctorMessageModal
        isOpen={showDoctorModal}
        onClose={() => setShowDoctorModal(false)}
        onSubmit={handleDoctorMessageSubmit}
        provider={selectedProvider}
        careType={careType}
      />
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        onSubmit={handleReviewSubmit}
        provider={selectedProvider}
      />
      <ConsultModal
        isOpen={showConsultModal}
        onClose={() => setShowConsultModal(false)}
        provider={selectedProvider}
      />
      <ProviderClaimModal
        open={showClaimModal}
        provider={selectedProvider}
        onClose={() => setShowClaimModal(false)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Lighthouse Loader — shown while search is in flight
// Animated compass/lighthouse mark with beam glow + rotating ring
// ─────────────────────────────────────────────────────────────────
function LighthouseLoader() {
  return (
    <div
      data-testid="lighthouse-loader"
      className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-10 md:p-14 flex flex-col items-center justify-center"
      role="status"
      aria-live="polite"
    >
      <style>{`
        @keyframes lh-beam {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50%      { opacity: 0.85; transform: scale(1.12); }
        }
        @keyframes lh-sweep {
          0%   { transform: rotate(0deg);   opacity: 0.15; }
          50%  { opacity: 0.55; }
          100% { transform: rotate(360deg); opacity: 0.15; }
        }
        .lh-beam-glow { animation: lh-beam 2.2s ease-in-out infinite; }
        .lh-beam-sweep { animation: lh-sweep 4.5s linear infinite; }
      `}</style>

      <div className="relative w-28 h-28 md:w-36 md:h-36">
        {/* Warm coral beam glow behind the mark, breathing */}
        <div
          className="absolute inset-0 rounded-full bg-secondary/40 blur-2xl lh-beam-glow"
          aria-hidden="true"
        />
        {/* Sweeping conic light — mimics a lighthouse beam rotating */}
        <div
          className="absolute -inset-4 rounded-full lh-beam-sweep pointer-events-none"
          style={{
            background:
              "conic-gradient(from 0deg, rgba(224,123,84,0) 0deg, rgba(224,123,84,0.55) 30deg, rgba(224,123,84,0) 90deg, rgba(224,123,84,0) 360deg)",
            maskImage: "radial-gradient(circle, transparent 40%, black 42%)",
            WebkitMaskImage: "radial-gradient(circle, transparent 40%, black 42%)",
          }}
          aria-hidden="true"
        />
        {/* Dashed ring, slowly rotating opposite direction */}
        <div
          className="absolute -inset-1 rounded-full border-2 border-dashed border-primary/40"
          style={{ animation: "spin 9s linear infinite reverse" }}
          aria-hidden="true"
        />
        {/* Compass / lighthouse medallion */}
        <div className="absolute inset-2 rounded-full overflow-hidden ring-4 ring-primary/20 shadow-md">
          <img
            src="/tyvena-mark-512.png"
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>
      </div>

      <p className="mt-6 text-lg md:text-xl font-heading font-bold text-accent text-center">
        Opening the door to your providers…
      </p>
      <p className="mt-1.5 text-sm text-text-secondary font-body italic text-center">
        Finding your providers, please hold on a moment.
      </p>
    </div>
  );
}
